import { View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import {
  Colors,
  Flex,
  IconCircleDeleteFilled,
  IconMenu,
  Typography,
} from '@banhala/ui-kit';
import type { LikeFolder } from 'shared/src/api/likedFolderService/getLikedFolders/types';
import {
  DRAGGABLE_ITEM_HEIGHT,
  type Positions,
} from './DraggableFolderList';
import { FolderThumbnail } from './FolderThumbnail';

const TRANSITION_TIMING = {
  duration: 180,
  easing: Easing.out(Easing.quad),
};

const DRAG_SCALE_TIMING = { duration: 150 };

interface FolderOrderEditItemProps {
  folder: LikeFolder;
  initialIndex: number;
  positions: SharedValue<Positions>;
  itemsCount: number;
  onPressDelete: () => void;
  onDragEnd: () => void;
}

export const FolderOrderEditItem = ({
  folder,
  initialIndex,
  positions,
  itemsCount,
  onPressDelete,
  onDragEnd,
}: FolderOrderEditItemProps) => {
  const isDragging = useSharedValue(false);
  const translateY = useSharedValue(initialIndex * DRAGGABLE_ITEM_HEIGHT);
  const dragStartY = useSharedValue(0);

  useAnimatedReaction(
    () => positions.value[folder.sno],
    (current, previous) => {
      if (current == null || current === previous || isDragging.value) return;
      translateY.value = withTiming(
        current * DRAGGABLE_ITEM_HEIGHT,
        TRANSITION_TIMING,
      );
    },
  );

  const dragGesture = Gesture.Pan()
    .activateAfterLongPress(150)
    .onStart(() => {
      const currentIndex = positions.value[folder.sno] ?? initialIndex;
      dragStartY.value = currentIndex * DRAGGABLE_ITEM_HEIGHT;
      isDragging.value = true;
    })
    .onUpdate(event => {
      const currentIndex = positions.value[folder.sno];
      if (currentIndex == null) return;

      const nextY = dragStartY.value + event.translationY;
      translateY.value = nextY;

      const targetIndex = Math.min(
        Math.max(Math.round(nextY / DRAGGABLE_ITEM_HEIGHT), 0),
        itemsCount - 1,
      );
      if (currentIndex === targetIndex) return;

      const next: Positions = { ...positions.value };
      for (const key in next) {
        const sno = Number(key);
        if (sno === folder.sno) continue;
        const idx = next[sno];
        if (currentIndex < targetIndex) {
          if (idx > currentIndex && idx <= targetIndex) next[sno] = idx - 1;
        } else if (idx >= targetIndex && idx < currentIndex) {
          next[sno] = idx + 1;
        }
      }
      next[folder.sno] = targetIndex;
      positions.value = next;
    })
    .onEnd(() => {
      const finalY = (positions.value[folder.sno] ?? 0) * DRAGGABLE_ITEM_HEIGHT;
      translateY.value = withTiming(finalY, TRANSITION_TIMING);
      isDragging.value = false;
      runOnJS(onDragEnd)();
    });

  const containerStyle = useAnimatedStyle(() => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: DRAGGABLE_ITEM_HEIGHT,
    transform: [
      { translateY: translateY.value },
      { scale: withTiming(isDragging.value ? 1.02 : 1, DRAG_SCALE_TIMING) },
    ],
    zIndex: isDragging.value ? 1 : 0,
    elevation: isDragging.value ? 4 : 0,
    backgroundColor: Colors.content_always_white,
    shadowColor: Colors.content_primary,
    shadowOpacity: withTiming(
      isDragging.value ? 0.15 : 0,
      DRAG_SCALE_TIMING,
    ),
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
  }));

  return (
    <Animated.View style={containerStyle}>
      <Flex horizontal height={DRAGGABLE_ITEM_HEIGHT} alignItems={'center'}>
        <Flex.Center
          width={56}
          height={DRAGGABLE_ITEM_HEIGHT}
          onPress={onPressDelete}
        >
          <IconCircleDeleteFilled size={20} fill={Colors.content_negative} />
        </Flex.Center>
        <FolderThumbnail folder={folder} />
        <Flex
          horizontal
          flex={1}
          alignItems={'center'}
          padding={{ left: 8 }}
        >
          <Flex flexShrink={1}>
            <Typography
              type={'subtitle2'}
              color={'content_primary'}
              numberOfLines={1}
              ellipsizeMode={'tail'}
            >
              {folder.title}
            </Typography>
          </Flex>
          <Flex padding={{ left: 2 }}>
            <Typography type={'body3'} color={'content_tertiary'}>
              {`(${folder.count})`}
            </Typography>
          </Flex>
        </Flex>
        <GestureDetector gesture={dragGesture}>
          <View
            collapsable={false}
            style={{
              width: 56,
              height: DRAGGABLE_ITEM_HEIGHT,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconMenu size={20} fill={Colors.content_secondary} />
          </View>
        </GestureDetector>
      </Flex>
    </Animated.View>
  );
};
